/**
 * Collapsible DocuSign Envelopes panel — shared by the UK & AUS Onboarding
 * Centres so both regions look identical. Shows each envelope with a status
 * badge, sent date and signed/viewed/pending summary; expand a row to see the
 * per-signer table and envelope ID footer. Optional Resume-in-DocuSign button
 * for draft ("created") envelopes.
 */
import { useState } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronDown, ChevronRight, FileSignature, Filter, RefreshCw, Loader2, AlertCircle, CheckCircle } from "lucide-react";
import { EnvelopeActions } from "./EnvelopeActions";

export const DocuSignEnvelopesPanel = ({
  envelopes = [],
  loading = false,
  error = null,
  onRefresh,
  locale = "en-GB",
  onResume,
  resumingEnvelopeId = null,
  defaultExpanded = true,
  compact = false,
  onExpandChange = null,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [filter, setFilter] = useState("open");
  const [expandedEnvelopes, setExpandedEnvelopes] = useState({});

  const toggleExpanded = () => {
    const next = !expanded;
    setExpanded(next);
    if (onExpandChange) onExpandChange(next);
  };

  const toggleEnvelopeExpand = (id) =>
    setExpandedEnvelopes((prev) => ({ ...prev, [id]: !prev[id] }));

  const openCount = envelopes.filter(
    (e) => e.status !== "completed" && e.status !== "voided" && e.status !== "declined"
  ).length;

  const fmtDate = (d, withYear = false) =>
    d ? new Date(d).toLocaleDateString(locale, { day: "numeric", month: "short", ...(withYear ? { year: "numeric" } : {}) }) : "-";

  return (
    <Card className={compact ? "mt-4" : "mt-6"} data-testid="docusign-envelopes-panel">
      <CardHeader
        className="pb-3 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={toggleExpanded}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {expanded ? <ChevronDown className="w-5 h-5 text-gray-500" /> : <ChevronRight className="w-5 h-5 text-gray-500" />}
            <FileSignature className="w-5 h-5 text-blue-600" />
            <div>
              <CardTitle className={compact ? "text-base" : "text-lg"}>DocuSign Envelopes</CardTitle>
              {!compact && (
                <CardDescription>
                  Track document signing status
                  {!expanded && openCount > 0 && (
                    <span className="ml-2 text-blue-600">({openCount} open)</span>
                  )}
                </CardDescription>
              )}
              {compact && !expanded && openCount > 0 && (
                <CardDescription>
                  <span className="text-blue-600">{openCount} open</span>
                </CardDescription>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[130px] h-8 text-xs" data-testid="envelope-filter-select">
                <Filter className="w-3 h-3 mr-1" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open Only</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="all">All Envelopes</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading} data-testid="envelope-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600 mr-2" />
              <span className="text-muted-foreground">Loading envelopes...</span>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
              <p className="text-sm text-red-600">{error}</p>
              <Button variant="outline" size="sm" className="mt-2" onClick={onRefresh}>Try Again</Button>
            </div>
          ) : (() => {
            const filtered = envelopes.filter((env) => {
              if (filter === "open") return env.status !== "completed" && env.status !== "voided" && env.status !== "declined";
              if (filter === "completed") return env.status === "completed";
              return true;
            });

            if (filtered.length === 0) {
              return (
                <div className="text-center py-8 text-muted-foreground">
                  <FileSignature className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>
                    {filter === "open" ? "No open envelopes" :
                     filter === "completed" ? "No completed envelopes" :
                     "No envelopes found in the last 90 days"}
                  </p>
                </div>
              );
            }

            return (
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {filtered.map((env) => {
                  const isExpanded = expandedEnvelopes[env.envelope_id];
                  const signers = env.signers || [];
                  const signedCount = signers.filter((s) => s.signed_at).length;
                  const viewedCount = signers.filter((s) => s.delivered_at && !s.signed_at).length;
                  const totalSigners = signers.length;

                  return (
                    <div
                      key={env.envelope_id}
                      className="bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors overflow-hidden"
                      data-testid="envelope-row"
                    >
                      <div
                        className="p-3 cursor-pointer flex items-center gap-3 hover:bg-gray-100 transition-colors"
                        onClick={() => toggleEnvelopeExpand(env.envelope_id)}
                      >
                        {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-500 shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-gray-900 truncate">{env.subject}</span>
                            <Badge className={`shrink-0 text-xs ${
                              env.status === "completed" ? "bg-green-600" :
                              env.status === "delivered" ? "bg-blue-600" :
                              env.status === "declined" || env.status === "voided" ? "bg-red-600" :
                              env.status === "sent" ? "bg-amber-500" : "bg-gray-500"
                            }`}>
                              {env.status_label}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                            <span>{env.sent_at ? fmtDate(env.sent_at) : "Not sent"}</span>
                            <span className="text-gray-400">•</span>
                            <span>
                              {signedCount > 0 && <span className="text-green-600">{signedCount} signed</span>}
                              {signedCount > 0 && viewedCount > 0 && ", "}
                              {viewedCount > 0 && <span className="text-blue-600">{viewedCount} viewed</span>}
                              {signedCount === 0 && viewedCount === 0 && <span>{totalSigners} pending</span>}
                              {signedCount > 0 && signedCount < totalSigners && `, ${totalSigners - signedCount - viewedCount} pending`}
                            </span>
                          </div>
                        </div>
                        <EnvelopeActions envelope={env} onChanged={onRefresh} />
                        {env.status === "created" && onResume && (
                          <Button
                            variant="default"
                            size="sm"
                            className="shrink-0 bg-blue-600 hover:bg-blue-700 text-white"
                            data-testid={`resume-envelope-${env.envelope_id}`}
                            disabled={resumingEnvelopeId === env.envelope_id}
                            onClick={(e) => { e.stopPropagation(); onResume(env.envelope_id); }}
                          >
                            {resumingEnvelopeId === env.envelope_id ? (
                              <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Opening…</>
                            ) : (
                              <><FileSignature className="w-3.5 h-3.5 mr-1" />Resume in DocuSign</>
                            )}
                          </Button>
                        )}
                      </div>

                      {isExpanded && (
                        <div className="px-3 pb-3 pt-0">
                          <div className="bg-white rounded-md border">
                            <div className="px-3 py-2 bg-gray-100 border-b text-xs font-medium text-gray-600 grid grid-cols-12 gap-2">
                              <span className="col-span-4">Signer</span>
                              <span className="col-span-4">Email</span>
                              <span className="col-span-2">Status</span>
                              <span className="col-span-2">Date</span>
                            </div>
                            {signers.length === 0 && (
                              <div className="px-3 py-3 text-sm text-muted-foreground">No signer details available</div>
                            )}
                            {signers.map((signer, idx) => {
                              const signerStatus = signer.signed_at ? "Signed" :
                                signer.delivered_at ? "Viewed" :
                                signer.status === "declined" ? "Declined" :
                                signer.status === "sent" ? "Sent" : "Pending";
                              const statusDate = signer.signed_at || signer.delivered_at;
                              return (
                                <div key={idx} className={`px-3 py-2 text-sm grid grid-cols-12 gap-2 items-center ${idx < signers.length - 1 ? "border-b" : ""}`}>
                                  <span className="col-span-4 font-medium truncate" title={signer.name}>{signer.name}</span>
                                  <span className="col-span-4 text-muted-foreground truncate" title={signer.email}>{signer.email}</span>
                                  <span className={`col-span-2 inline-flex items-center gap-1 text-xs font-medium ${
                                    signerStatus === "Signed" ? "text-green-600" :
                                    signerStatus === "Viewed" ? "text-blue-600" :
                                    signerStatus === "Declined" ? "text-red-600" :
                                    signerStatus === "Sent" ? "text-amber-600" : "text-gray-500"
                                  }`}>
                                    {(signerStatus === "Signed" || signerStatus === "Viewed") && <CheckCircle className="w-3 h-3" />}
                                    {signerStatus}
                                  </span>
                                  <span className="col-span-2 text-xs text-muted-foreground">{fmtDate(statusDate)}</span>
                                </div>
                              );
                            })}
                          </div>
                          <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                            <span className="font-mono">{env.envelope_id}</span>
                            {env.status === "completed" && env.completed_at && (
                              <span className="text-green-600 flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" />
                                Completed {fmtDate(env.completed_at, true)}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </CardContent>
      )}
    </Card>
  );
};
